import React from "react";
import {
  Typography,
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  Divider,
  Grid,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import SyncIcon from "@mui/icons-material/Sync";
import LinkIcon from "@mui/icons-material/Link";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HomeIcon from "@mui/icons-material/Home";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import { api, type ApiAccount, type ApiAccountTag } from "../services/api";

// ── Account behavior metadata ───────────────────────────────
type AccountBehavior = ApiAccount["behavior"];

const BEHAVIOR_META: Record<
  AccountBehavior,
  { label: string; icon: React.ReactElement<any>; netWorthSign: "asset" | "liability" }
> = {
  cash:       { label: "Cash / Bank",       icon: <AccountBalanceIcon />,  netWorthSign: "asset" },
  credit:     { label: "Credit / Revolving",icon: <CreditCardIcon />,      netWorthSign: "liability" },
  asset:      { label: "Physical Asset",    icon: <HomeIcon />,            netWorthSign: "asset" },
  investment: { label: "Investment",        icon: <TrendingUpIcon />,      netWorthSign: "asset" },
  liability:  { label: "Loan / Liability",  icon: <ReceiptLongIcon />,     netWorthSign: "liability" },
};

const ALL_BEHAVIORS = Object.keys(BEHAVIOR_META) as AccountBehavior[];
const ASSET_BEHAVIORS: AccountBehavior[] = ["cash", "asset", "investment"];
const LIABILITY_BEHAVIORS: AccountBehavior[] = ["credit", "liability"];

function formatDollars(cents: number) {
  const abs = Math.abs(cents / 100);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return cents < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDelta(cents: number, absolute = false) {
  const value = absolute ? Math.abs(cents) : cents;
  return `${cents >= 0 ? '+' : '-'}$${Math.abs(value / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── Add / Edit Dialog ───────────────────────────────────────
interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    behavior: AccountBehavior;
    classification?: string;
    institution?: string;
    balanceCents: number;
    originalBalanceCents?: number;
    notes?: string;
  }) => Promise<void>;
  initial?: ApiAccount | null;
}

function AccountDialog({ open, onClose, onSave, initial }: AccountDialogProps) {
  const [name, setName] = React.useState("");
  const [behavior, setBehavior] = React.useState<AccountBehavior>("cash");
  const [classification, setClassification] = React.useState("");
  const [institution, setInstitution] = React.useState("");
  const [balanceStr, setBalanceStr] = React.useState("");
  const [originalBalanceStr, setOriginalBalanceStr] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      if (initial) {
        setName(initial.name);
        setBehavior(initial.behavior);
        setClassification(initial.classification ?? "");
        setInstitution(initial.institution ?? "");
        setBalanceStr((initial.balanceCents / 100).toFixed(2));
        setOriginalBalanceStr(
          initial.originalBalanceCents != null
            ? (initial.originalBalanceCents / 100).toFixed(2)
            : ""
        );
        setNotes(initial.notes ?? "");
      } else {
        setName("");
        setBehavior("cash");
        setClassification("");
        setInstitution("");
        setBalanceStr("");
        setOriginalBalanceStr("");
        setNotes("");
      }
      setError(null);
    }
  }, [open, initial]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    const parsed = parseFloat(balanceStr || "0");
    if (isNaN(parsed)) {
      setError("Invalid balance amount");
      return;
    }
    const parsedOriginal = originalBalanceStr
      ? parseFloat(originalBalanceStr)
      : undefined;
    if (originalBalanceStr && isNaN(parsedOriginal!)) {
      setError("Invalid original balance amount");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        behavior,
        classification: classification.trim() || undefined,
        institution: institution.trim() || undefined,
        balanceCents: Math.round(parsed * 100),
        originalBalanceCents:
          parsedOriginal != null ? Math.round(parsedOriginal * 100) : undefined,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to save account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? "Edit Account" : "Add Account"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            select
            label="Account Category"
            value={behavior}
            onChange={(e) => setBehavior(e.target.value as AccountBehavior)}
            fullWidth
            helperText="Determines how this account affects net worth"
          >
            {ALL_BEHAVIORS.map((b) => (
              <MenuItem key={b} value={b}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {React.cloneElement(BEHAVIOR_META[b].icon, { fontSize: "small" })}
                  <span>{BEHAVIOR_META[b].label}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Classification"
            value={classification}
            onChange={(e) => setClassification(e.target.value)}
            fullWidth
            placeholder={
              behavior === "cash" ? "e.g. Checking, Savings, Cash" :
              behavior === "asset" ? "e.g. Vehicle, Real Estate, Jewelry" :
              behavior === "investment" ? "e.g. Brokerage, 401k, IRA, Crypto" :
              behavior === "credit" ? "e.g. Credit Card, HELOC" :
              "e.g. Mortgage, Auto Loan, Student Loan"
            }
            helperText="Optional label for organization"
          />
          <TextField
            label="Institution"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            fullWidth
            placeholder="e.g. Chase, Vanguard"
          />
          <TextField
            label="Current Balance ($)"
            value={balanceStr}
            onChange={(e) => setBalanceStr(e.target.value)}
            fullWidth
            type="number"
            helperText="Use negative for debts (credit cards, loans)"
          />
          <TextField
            label="Original Balance / Cost Basis ($)"
            value={originalBalanceStr}
            onChange={(e) => setOriginalBalanceStr(e.target.value)}
            fullWidth
            type="number"
            helperText="Purchase price, original loan amount, or cost basis"
          />
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : initial ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete Confirmation Dialog ──────────────────────────────
interface DeleteDialogProps {
  open: boolean;
  account: ApiAccount | null;
  onClose: () => void;
  onArchive: () => Promise<void>;
  onForceDelete: () => Promise<void>;
}

function DeleteAccountDialog({ open, account, onClose, onArchive, onForceDelete }: DeleteDialogProps) {
  const [processing, setProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleArchive = async () => {
    setProcessing(true);
    setError(null);
    try {
      await onArchive();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to archive account");
    } finally {
      setProcessing(false);
    }
  };

  const handleForceDelete = async () => {
    setProcessing(true);
    setError(null);
    try {
      await onForceDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to delete account");
    } finally {
      setProcessing(false);
    }
  };

  const isArchived = account?.status === "archived" || account?.status === "closed";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isArchived ? "Delete Account" : "Archive or Delete Account"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isArchived ? (
          <Typography>
            <strong>{account?.name}</strong> is already archived. Do you want to permanently
            delete it? This action cannot be undone.
          </Typography>
        ) : (
          <>
            <Typography gutterBottom>
              What would you like to do with <strong>{account?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Archive:</strong> Hides the account from active views but preserves all data
              and history. You can restore it later.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Delete permanently:</strong> Removes the account forever. Only possible if
              it has no transactions.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        {!isArchived && (
          <Button
            onClick={handleArchive}
            color="warning"
            variant="outlined"
            startIcon={<ArchiveIcon />}
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : "Archive"}
          </Button>
        )}
        <Button
          onClick={handleForceDelete}
          color="error"
          variant="contained"
          disabled={processing}
        >
          {processing ? <CircularProgress size={20} /> : "Delete Permanently"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ───────────────────────────────────────────────
export default function AccountsPage({
  setHeaderActions,
}: {
  setHeaderActions: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}) {
  const [accounts, setAccounts] = React.useState<ApiAccount[]>([]);
  const [allTags, setAllTags] = React.useState<ApiAccountTag[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showArchived, setShowArchived] = React.useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingAccount, setEditingAccount] = React.useState<ApiAccount | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<ApiAccount | null>(null);

  const [filterType, setFilterType] = React.useState<"All" | AccountBehavior>("All");
  const [historyMonths, setHistoryMonths] = React.useState(6);

  // ── Fetch ──────────────────────────────────────────────
  const [netWorthSnapshots, setNetWorthSnapshots] = React.useState<
    Array<{
      date: string;
      totalAssetsCents: number;
      totalLiabilitiesCents: number;
      netWorthCents: number;
    }>
  >([]);

  const fetchAccounts = React.useCallback(async () => {
    try {
      setError(null);
      const status = showArchived ? 'all' : 'active';
      const { accounts: data } = await api.getAccounts(status);
      setAccounts(data);
    } catch (err: any) {
      setError(err.message || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const fetchTags = React.useCallback(async () => {
    try {
      const { tags } = await api.getAccountTags();
      setAllTags(tags);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchNetWorthHistory = React.useCallback(async () => {
    try {
      const { snapshots } = await api.getNetWorthHistory(historyMonths);
      setNetWorthSnapshots(snapshots);
    } catch {
      // Non-critical — chart simply won't show data
    }
  }, [historyMonths]);

  React.useEffect(() => {
    fetchAccounts();
    fetchTags();
  }, [fetchAccounts, fetchTags]);

  React.useEffect(() => {
    fetchNetWorthHistory();
  }, [fetchNetWorthHistory]);

  // ── CRUD handlers ──────────────────────────────────────
  const handleSave = async (data: {
    name: string;
    behavior: AccountBehavior;
    classification?: string;
    institution?: string;
    balanceCents: number;
    originalBalanceCents?: number;
    notes?: string;
  }) => {
    if (editingAccount) {
      const { account } = await api.updateAccount(editingAccount.id, data);
      setAccounts((prev) => prev.map((a) => (a.id === account.id ? account : a)));
    } else {
      const { account } = await api.createAccount(data);
      setAccounts((prev) => [...prev, account]);
    }
  };

  const handleArchive = async () => {
    if (!deleteTarget) return;
    await api.deleteAccount(deleteTarget.id); // soft delete (archive)
    setAccounts((prev) =>
      prev.map((a) =>
        a.id === deleteTarget.id ? { ...a, status: "archived" as const, closedAt: new Date().toISOString() } : a
      )
    );
    if (!showArchived) {
      setAccounts((prev) => prev.filter((a) => a.status === "active"));
    }
  };

  const handleForceDelete = async () => {
    if (!deleteTarget) return;
    await api.deleteAccount(deleteTarget.id, true);
    setAccounts((prev) => prev.filter((a) => a.id !== deleteTarget.id));
  };

  const handleRestore = async (account: ApiAccount) => {
    await api.restoreAccount(account.id);
    setAccounts((prev) =>
      prev.map((a) =>
        a.id === account.id ? { ...a, status: "active" as const, closedAt: null } : a
      )
    );
  };

  const openAdd = () => {
    setEditingAccount(null);
    setDialogOpen(true);
  };

  const openEdit = (a: ApiAccount) => {
    setEditingAccount(a);
    setDialogOpen(true);
  };

  // ── Header action button ──────────────────────────────
  React.useEffect(() => {
    setHeaderActions(
      <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
        Add Account
      </Button>
    );
    return () => setHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderActions]);

  // ── Computed values ────────────────────────────────────
  const filtered =
    filterType === "All" ? accounts : accounts.filter((a) => a.behavior === filterType);

  // Net worth uses only active accounts
  const activeFiltered = filtered.filter((a) => a.status === "active");

  const totalAssets = activeFiltered
    .filter((a) => ASSET_BEHAVIORS.includes(a.behavior))
    .reduce((s, a) => s + a.balanceCents, 0);
  const totalLiabilities = activeFiltered
    .filter((a) => LIABILITY_BEHAVIORS.includes(a.behavior))
    .reduce((s, a) => s + a.balanceCents, 0);
  const netWorth = totalAssets + totalLiabilities;

  // Group accounts by behavior
  const grouped = React.useMemo(() => {
    const map = new Map<AccountBehavior, ApiAccount[]>();
    filtered.forEach((a) => {
      const list = map.get(a.behavior) ?? [];
      list.push(a);
      map.set(a.behavior, list);
    });
    return map;
  }, [filtered]);

  // Occupied behaviors (for the filter dropdown)
  const occupiedBehaviors = React.useMemo(
    () => Array.from(new Set(accounts.map((a) => a.behavior))),
    [accounts]
  );

  const netWorthHistory = React.useMemo(() => {
    if (netWorthSnapshots.length === 0) return [];
    // Thin: weekly for >=6M, every 2 days for 3M, daily for 1M
    const step = historyMonths >= 6 ? 7 : historyMonths >= 3 ? 2 : 1;
    return netWorthSnapshots
      .filter((_, i) => i === 0 || i === netWorthSnapshots.length - 1 || i % step === 0)
      .map((s) => ({
        date: s.date,
        label: new Date(s.date + 'T00:00:00').toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        assets: Math.round(s.totalAssetsCents / 100),
        liabilities: Math.round(Math.abs(s.totalLiabilitiesCents) / 100),
        netWorth: Math.round(s.netWorthCents / 100),
      }));
  }, [netWorthSnapshots, historyMonths]);

  const monthlyComparison = React.useMemo(() => {
    if (netWorthSnapshots.length === 0) {
      return {
        netWorthChange: 0,
        assetsChange: 0,
        liabilitiesChange: 0,
      };
    }

    const latest = netWorthSnapshots[netWorthSnapshots.length - 1];
    const latestDate = new Date(`${latest.date}T00:00:00`);
    const targetTime = latestDate.getTime() - 28 * 24 * 60 * 60 * 1000;

    const baseline = [...netWorthSnapshots]
      .reverse()
      .find((snapshot) => new Date(`${snapshot.date}T00:00:00`).getTime() <= targetTime)
      ?? netWorthSnapshots[0];

    return {
      netWorthChange: latest.netWorthCents - baseline.netWorthCents,
      assetsChange: latest.totalAssetsCents - baseline.totalAssetsCents,
      liabilitiesChange: Math.abs(latest.totalLiabilitiesCents) - Math.abs(baseline.totalLiabilitiesCents),
    };
  }, [netWorthSnapshots]);

  const accountColumns = React.useMemo(() => {
    const sectionOrder: Record<string, { title: string; behaviors: AccountBehavior[]; icon: React.ReactElement<any> }> = {
      cash: { title: 'Cash', behaviors: ['cash'], icon: <AccountBalanceIcon /> },
      investments: { title: 'Investments', behaviors: ['investment'], icon: <TrendingUpIcon /> },
      assets: { title: 'Property & Assets', behaviors: ['asset'], icon: <HomeIcon /> },
      credit: { title: 'Credit Cards', behaviors: ['credit'], icon: <CreditCardIcon /> },
      loans: { title: 'Loans & Mortgages', behaviors: ['liability'], icon: <ReceiptLongIcon /> },
    };

    const buildSection = (key: keyof typeof sectionOrder) => {
      const config = sectionOrder[key];
      const items = config.behaviors.flatMap((behavior) => grouped.get(behavior) ?? []);
      return {
        key,
        title: config.title,
        icon: config.icon,
        items,
        total: items.reduce((sum, account) => sum + account.balanceCents, 0),
      };
    };

    return {
      left: [buildSection('cash'), buildSection('investments'), buildSection('assets')],
      right: [buildSection('credit'), buildSection('loans')],
    };
  }, [grouped]);

  // ── Render ─────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10, width: '100%', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4, px: 4, width: '100%', height: '100%', overflowY: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        mt: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2, md: 4 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Box sx={{ mt: 3, mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
        {[
          {
            title: 'Net Worth',
            value: formatDollars(netWorth),
            change: monthlyComparison.netWorthChange,
          },
          {
            title: 'Total Assets',
            value: formatDollars(totalAssets),
            change: monthlyComparison.assetsChange,
          },
          {
            title: 'Total Liabilities',
            value: formatDollars(Math.abs(totalLiabilities) * -1),
            change: monthlyComparison.liabilitiesChange,
            absoluteChange: true,
          },
        ].map((tile) => {
          const positive = tile.change >= 0;
          return (
            <Card key={tile.title} sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {tile.title}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {tile.value}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {positive ? (
                    <NorthEastIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  ) : (
                    <SouthEastIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  )}
                  <Typography variant="body2" sx={{ color: positive ? 'success.main' : 'error.main', fontWeight: 600 }}>
                    {formatDelta(tile.change, tile.absoluteChange)} this month
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Show archived</Typography>}
          sx={{ mr: 0 }}
        />
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
          <InputLabel>Filter</InputLabel>
          <Select
            native
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            label="Filter"
          >
            <option value="All">All</option>
            {occupiedBehaviors.map((b: AccountBehavior) => (
              <option key={b} value={b}>
                {BEHAVIOR_META[b].label}
              </option>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        {[{ title: 'Assets', sections: accountColumns.left }, { title: 'Liabilities', sections: accountColumns.right }].map((column) => (
          <Box key={column.title}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1.1 }}>
              {column.title}
            </Typography>

            <Stack spacing={3} sx={{ mt: 1 }}>
              {column.sections.map((section) => {
                if (section.items.length === 0) return null;

                return (
                  <Box key={section.key}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto !important', fontWeight: 600 }}>
                        {formatDollars(section.total)}
                      </Typography>
                    </Stack>

                    <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                      <Stack divider={<Divider flexItem />}>
                        {section.items.map((a) => {
                          const isArchived = a.status === "archived" || a.status === "closed";
                          const subtitleParts = [a.classification, a.institution].filter(Boolean);
                          if (a.lastSyncedAt) {
                            const syncedDate = new Date(a.lastSyncedAt);
                            subtitleParts.push(`Updated ${syncedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`);
                          }

                          return (
                            <Box
                              key={a.id}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                px: 2,
                                py: 1.5,
                                opacity: isArchived ? 0.6 : 1,
                                borderLeft: isArchived ? '3px solid' : 'none',
                                borderColor: isArchived ? 'warning.main' : undefined,
                              }}
                            >
                              <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1.5,
                                    bgcolor: a.behavior === 'credit' || a.behavior === 'liability' ? 'rgba(255, 152, 0, 0.12)' : 'rgba(0, 166, 118, 0.10)',
                                    color: a.behavior === 'credit' || a.behavior === 'liability' ? '#B26A00' : '#067A58',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  {React.cloneElement(BEHAVIOR_META[a.behavior].icon, { fontSize: 'small' })}
                                </Box>

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                                      {a.name}
                                    </Typography>
                                    {a.isLinked && (
                                      <Tooltip title={`Linked${a.lastSyncedAt ? ` · Last synced ${new Date(a.lastSyncedAt).toLocaleDateString()}` : ''}`}>
                                        <LinkIcon sx={{ fontSize: 14, color: 'info.main' }} />
                                      </Tooltip>
                                    )}
                                    {isArchived && (
                                      <Chip label="Archived" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                  </Stack>
                                  {subtitleParts.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {subtitleParts.join(' • ')}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>

                              <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 96, textAlign: 'right' }}>
                                  {formatDollars(a.balanceCents)}
                                </Typography>
                                {isArchived ? (
                                  <Tooltip title="Restore">
                                    <IconButton size="small" onClick={() => handleRestore(a)}>
                                      <UnarchiveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => openEdit(a)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={isArchived ? 'Delete permanently' : 'Archive / Delete'}>
                                  <IconButton size="small" onClick={() => setDeleteTarget(a)}>
                                    {isArchived ? <DeleteIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Card>
                  </Box>
                );
              })}

              {column.sections.every((section) => section.items.length === 0) && (
                <Card sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No {column.title.toLowerCase()} match the current filter.
                  </Typography>
                </Card>
              )}
            </Stack>
          </Box>
        ))}
      </Box>

      {accounts.length === 0 && (
        <Card sx={{ p: 4, textAlign: "center", mt: 3, borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No accounts yet
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openAdd}>
            Add your first account
          </Button>
        </Card>
      )}

      {/* Dialogs */}
      <AccountDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initial={editingAccount}
      />
      <DeleteAccountDialog
        open={!!deleteTarget}
        account={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onArchive={handleArchive}
        onForceDelete={handleForceDelete}
      />
    </Box>
  );
}

